'use client';

import { useEffect, useRef, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { TrackedJob, JobStatus, JobStats, BulkResult } from '@/context/JobContext';

interface UseJobSubscriptionOptions {
  userId: string | null;
  onJobUpdate: (job: TrackedJob) => void;
  onJobInsert: (job: TrackedJob) => void;
}

function rowToTrackedJob(row: Record<string, any>): TrackedJob {
  return {
    id: row.id,
    prompt: row.prompt || '',
    status: row.status as JobStatus,
    progress: row.progress ?? 0,
    currentStage: row.current_stage ?? 'Queued',
    stats: typeof row.stats === 'object' ? row.stats as JobStats : JSON.parse(row.stats || '{}'),
    results: Array.isArray(row.results) ? row.results as BulkResult[] : JSON.parse(row.results || '[]'),
    startedAt: row.started_at ?? Date.now(),
    completedAt: row.completed_at ?? undefined,
  };
}

export function useJobSubscription({ userId, onJobUpdate, onJobInsert }: UseJobSubscriptionOptions) {
  const supabase = createClient();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const subscribe = useCallback(() => {
    if (!userId || channelRef.current) return;

    const channel = supabase
      .channel(`bulk_search_jobs:user:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bulk_search_jobs',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const job = rowToTrackedJob(payload.new as Record<string, any>);
          onJobInsert(job);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bulk_search_jobs',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const job = rowToTrackedJob(payload.new as Record<string, any>);
          onJobUpdate(job);
        }
      )
      .subscribe();

    channelRef.current = channel;
  }, [userId, onJobUpdate, onJobInsert, supabase]);

  const unsubscribe = useCallback(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, [supabase]);

  useEffect(() => {
    subscribe();
    return () => {
      unsubscribe();
    };
  }, [subscribe, unsubscribe]);

  return { unsubscribe };
}

export async function persistJobToSupabase(
  supabase: ReturnType<typeof createClient>,
  job: TrackedJob,
  userId: string
) {
  const { error } = await supabase.from('bulk_search_jobs').upsert(
    {
      id: job.id,
      user_id: userId,
      prompt: job.prompt,
      companies: [],
      purposes: [],
      max_results: job.stats.total,
      status: job.status,
      progress: job.progress,
      current_stage: job.currentStage,
      stats: job.stats,
      results: job.results,
      started_at: job.startedAt,
      completed_at: job.completedAt ?? null,
    },
    { onConflict: 'id' }
  );
  if (error) {
    // Non-blocking — SSE is still the primary update mechanism
    console.warn('[JobSync] Supabase upsert failed:', error.message);
  }
}

export async function loadJobsFromSupabase(
  supabase: ReturnType<typeof createClient>,
  userId: string
): Promise<TrackedJob[]> {
  const { data, error } = await supabase
    .from('bulk_search_jobs')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) {
    console.warn('[JobSync] Failed to load jobs from Supabase:', error.message);
    return [];
  }

  return (data || []).map(rowToTrackedJob);
}
